/**
 * OfficePerformanceReport — /reports/offices
 *
 * Funil mensal por escritório: leads recebidas · estimativas enviadas ·
 * RTs agendadas · RTs realizadas · propostas apresentadas · propostas
 * ganhas (caução mínima paga). Período "mês inicial → mês final", filtro
 * por escritório e export PDF / Excel.
 */
import { useEffect, useState } from "react";
import { Loader2, FileDown, FileSpreadsheet, Building2, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { METRICS, pct } from "@/lib/officeReport";
import OfficeReportMatrix from "@/components/officeReport/OfficeReportMatrix";
import OfficeReportRanking from "@/components/officeReport/OfficeReportRanking";
import MonthRangePicker, { PRESETS } from "@/components/officeReport/MonthRangePicker";

const fmt = (n) => (n || 0).toLocaleString("pt-PT");

const RATE_HINT = {
  leads: null,
  estimativas: (r) => `${pct(r.estimativa_pct)} das leads`,
  rt_agendadas: (r) => `${pct(r.rt_agendada_pct)} das estimativas`,
  rt_realizadas: (r) => `${pct(r.rt_realizada_pct)} das agendadas`,
  propostas: (r) => `${pct(r.proposta_pct)} das RTs realizadas`,
  ganhos: (r) => `${pct(r.ganho_pct)} das propostas · ${pct(r.lead_to_ganho_pct)} das leads`,
};

const EXPORTS = [
  { ext: "pdf", label: "PDF", icon: FileDown, testid: "office-report-export-pdf", primary: true },
  { ext: "xlsx", label: "Excel", icon: FileSpreadsheet, testid: "office-report-export-xlsx", primary: false },
];

function ExportButton({ def, busy, disabled, onClick }) {
  const Icon = def.icon;
  const cls = def.primary
    ? "bg-foreground text-background hover:bg-foreground/90"
    : "bg-background text-foreground border border-border hover:bg-accent";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={def.testid}
      className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold disabled:opacity-50 transition-colors ${cls}`}
    >
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Icon className="w-3.5 h-3.5" />}
      {def.label}
    </button>
  );
}

export default function OfficePerformanceReport() {
  const [period, setPeriod] = useState(PRESETS[0].range());
  const [office, setOffice] = useState("");
  const [offices, setOffices] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [metric, setMetric] = useState("leads");
  const [exporting, setExporting] = useState("");

  const params = { start: period.start, end: period.end, office: office || undefined };
  const periodInvalid = period.start > period.end;

  useEffect(() => {
    if (periodInvalid) return undefined;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const r = await api.get("/reports/offices", { params });
        if (cancelled) return;
        setData(r.data);
        setErr("");
        if (!office) setOffices((r.data.offices || []).map((o) => o.office));
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || "Não foi possível carregar o relatório.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period.start, period.end, office]);

  const exportFile = async (ext) => {
    setExporting(ext);
    try {
      const r = await api.get(`/reports/offices/${ext}`, { params, responseType: "blob" });
      const cd = r.headers?.["content-disposition"] || "";
      const name = /filename="([^"]+)"/.exec(cd)?.[1] || `Caring-Desempenho-Escritorios.${ext}`;
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(ext === "pdf" ? "PDF gerado" : "Excel gerado");
    } catch (e) {
      toast.error(`Falha ao gerar ${ext === "pdf" ? "PDF" : "Excel"}`);
    } finally {
      setExporting("");
    }
  };

  const years = data?.years_available?.length ? data.years_available : [new Date().getFullYear()];

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto space-y-6" data-testid="office-report-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
            <Building2 className="w-5 h-5 text-foreground" strokeWidth={1.6} />
          </div>
          <div>
            <h1 className="font-ui text-2xl text-foreground" data-testid="office-report-title">Desempenho por Escritório</h1>
            <p className="text-sm text-muted-foreground">
              Leads · Estimativas · RTs agendadas · RTs realizadas · Propostas apresentadas · Propostas ganhas, mês a mês
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1.5">
            <select
              value={office}
              onChange={(e) => setOffice(e.target.value)}
              className="text-xs bg-background border border-border rounded-full px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-foreground/20"
              data-testid="office-report-office-select"
              title="Filtrar por escritório"
            >
              <option value="">Todos os escritórios</option>
              {offices.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
            {office && (
              <button type="button" onClick={() => setOffice("")} className="text-muted-foreground hover:text-foreground p-0.5 rounded" title="Limpar filtro" data-testid="office-report-office-clear">
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          {EXPORTS.map((d) => (
            <ExportButton key={d.ext} def={d} busy={exporting === d.ext} disabled={!!exporting || !data || periodInvalid} onClick={() => exportFile(d.ext)} />
          ))}
        </div>
      </div>

      <div className="bg-card border border-border rounded-2xl px-5 py-3">
        <MonthRangePicker value={period} onChange={setPeriod} years={years} />
      </div>

      {err && (
        <div className="bg-card border border-border rounded-2xl p-6 text-sm text-muted-foreground" data-testid="office-report-error">
          {err}
        </div>
      )}
      {loading && !data && (
        <div className="bg-card border border-border rounded-2xl p-6 flex items-center gap-2 text-sm text-muted-foreground" data-testid="office-report-loading">
          <Loader2 className="w-4 h-4 animate-spin" /> A carregar relatório…
        </div>
      )}

      {data && !err && (
        <div className={`space-y-6 transition-opacity ${loading ? "opacity-60" : ""}`}>
          <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4" data-testid="office-report-kpis">
            {METRICS.map((m) => (
              <button
                key={m.key}
                type="button"
                onClick={() => setMetric(m.key)}
                data-testid={`office-report-kpi-${m.key}`}
                className={`text-left rounded-xl border p-4 transition-colors ${metric === m.key ? "border-foreground/40 bg-accent" : "border-border bg-card hover:bg-accent/60"}`}
              >
                <div className="text-[10px] uppercase tracking-[0.18em] font-semibold" style={{ color: m.color }}>{m.label}</div>
                <div className="font-ui text-3xl mt-1 tabular-nums text-foreground">{fmt(data.grand_totals[m.key])}</div>
                {RATE_HINT[m.key] && (
                  <div className="text-[11px] text-muted-foreground mt-1">{RATE_HINT[m.key](data.grand_rates)}</div>
                )}
              </button>
            ))}
          </div>

          <OfficeReportMatrix data={data} metric={metric} onMetricChange={setMetric} />
          <OfficeReportRanking offices={data.offices} months={data.months} />

          <p className="text-[11px] text-muted-foreground/80" data-testid="office-report-footnote">
            Cada lead conta uma única vez por indicador, na data da primeira ocorrência. Leads migradas do CRM que entraram
            directamente numa fase posterior contam nas etapas anteriores na data em que atingiram essa fase.
            Proposta ganha = lead passou a<span className="font-medium"> negócio ganho</span> (caução mínima paga). Leads de teste são excluídas.
          </p>
        </div>
      )}
    </div>
  );
}
